import Swal from "sweetalert2";
import { apiUrl } from "../../api/config";
import { getStoredAuthToken } from "../../authSession";

export const ASSET_RELOCATED_EVENT = "sunny:assets-relocated";
const storageEventKey = "sunny:assets-relocated:last";
const assetRelocationDialog = Swal.mixin({
  confirmButtonColor: "#2563eb",
  cancelButtonColor: "#64748b",
  reverseButtons: true,
  focusCancel: true,
});
const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (character) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[character]));
const locations = {
  prompt_md: "題目文字", answer_md: "答案文字", solution_md: "詳解文字",
  "asset:prompt": "題目附圖", "asset:answer": "答案附圖", "asset:solution": "詳解附圖",
};

const post = async (path, payload) => {
  const token = getStoredAuthToken();
  const response = await fetch(`${apiUrl}/assets/${path}/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(payload),
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.success) {
    throw new Error(result?.error || "無法確認搬移結果，請在此視窗重試。");
  }
  return result;
};

export const buildRelocationPreviewHtml = (preview) => `
  <div class="asset-relocation-preview" style="text-align:left;font-size:14px;line-height:1.6">
    <p style="margin:0 0 16px">${preview.moves.length} 個檔案 · <strong>${preview.question_count} 題</strong> · ${preview.draft_count} 份已儲存草稿</p>
    <div style="max-height:48vh;overflow:auto;padding-right:8px">
      ${preview.moves.map((move) => `
        <section style="border:1px solid #cbd5e1;border-radius:12px;padding:14px;margin-bottom:12px">
          <strong style="overflow-wrap:anywhere">${escapeHtml(move.source_key.split("/").pop())}</strong>
          <div style="margin-top:8px;color:#64748b">原 URL</div>
          <div style="overflow-wrap:anywhere;font-family:monospace;font-size:12px">${escapeHtml(move.old_url)}</div>
          <div style="margin-top:8px;color:#2563eb">新 URL</div>
          <div style="overflow-wrap:anywhere;font-weight:500">${escapeHtml(move.target_key)}</div>
          <div style="overflow-wrap:anywhere;font-family:monospace;font-size:12px">${escapeHtml(move.new_url)}</div>
          ${preview.questions.filter((q) => q.references.some((ref) => ref.source_key === move.source_key)).map((q) => `
            <div style="border-top:1px solid #cbd5e1;margin-top:12px;padding-top:10px">
              <span>題目 UUID</span>
              <code style="display:block;overflow-wrap:anywhere;font-size:12px;user-select:all">${escapeHtml(q.id)}</code>
              <div style="margin-top:4px">${escapeHtml(q.preview)}</div>
              <small style="color:#64748b">${escapeHtml([...new Set(q.references.filter((ref) => ref.source_key === move.source_key).map((ref) => locations[ref.location] || ref.location))].join("、"))}</small>
            </div>`).join("")}
          ${preview.drafts.filter((draft) => draft.source_keys.includes(move.source_key)).map((draft) => `
            <div style="border-top:1px solid #cbd5e1;margin-top:12px;padding-top:10px">
              <div>草稿：${escapeHtml(draft.title)}</div>
              <code style="display:block;overflow-wrap:anywhere;font-size:12px">${escapeHtml(draft.id)}</code>
            </div>`).join("")}
        </section>`).join("")}
    </div>
    <p style="font-size:13px;color:#64748b;margin-bottom:0">${preview.question_count || preview.draft_count
      ? "確認後會同步更新以上引用，題目 UUID 不會變更。"
      : "目前未找到題庫或已儲存草稿引用。"}<br>外部網站、匯出文件與離線編輯內容需另行更新。取消不會搬移檔案。</p>
  </div>`;

export const confirmAssetRelocation = async (payload) => {
  const preview = await post("rename/preview", payload);
  const response = await assetRelocationDialog.fire({
    title: "確認搬移與題庫連動",
    html: buildRelocationPreviewHtml(preview),
    width: "min(720px, calc(100vw - 24px))",
    didOpen: (popup) => {
      popup.style.maxHeight = "calc(100dvh - 24px)";
      popup.style.overflow = "auto";
    },
    showCancelButton: true,
    focusCancel: true,
    confirmButtonText: preview.question_count ? `搬移並更新 ${preview.question_count} 題` : "確認搬移並同步引用",
    cancelButtonText: "取消",
    showLoaderOnConfirm: true,
    allowOutsideClick: () => !Swal.isLoading(),
    allowEscapeKey: () => !Swal.isLoading(),
    preConfirm: async () => {
      try {
        return await post("rename", { confirmation_id: preview.confirmation_id });
      } catch (error) {
        Swal.showValidationMessage(escapeHtml(error.message));
        return false;
      }
    },
  });
  if (!response.isConfirmed || !response.value?.success) return null;
  const result = response.value;
  const detail = { moves: result.moves, confirmation_id: preview.confirmation_id };
  window.dispatchEvent(new CustomEvent(ASSET_RELOCATED_EVENT, { detail }));
  try { localStorage.setItem(storageEventKey, JSON.stringify(detail)); } catch { /* Private browsing. */ }
  if (result.cleanup_pending?.length) {
    await assetRelocationDialog.fire({
      icon: "warning", title: "題庫已更新，部分原檔仍保留",
      text: `新 URL 已生效；${result.cleanup_pending.length} 個原檔因清理未完成、版本變更或仍有引用而保留。請重新整理後確認，勿直接刪除仍被引用的圖片。`,
      confirmButtonText: "知道了",
    });
  }
  return result;
};

export const subscribeToAssetRelocations = (handler) => {
  const receive = (detail) => {
    if (Array.isArray(detail?.moves) && detail.moves.length) handler(detail.moves);
  };
  const onEvent = (event) => receive(event.detail);
  const onStorage = (event) => {
    if (event.key !== storageEventKey || !event.newValue) return;
    try { receive(JSON.parse(event.newValue)); } catch { /* Ignore unrelated invalid data. */ }
  };
  window.addEventListener(ASSET_RELOCATED_EVENT, onEvent);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(ASSET_RELOCATED_EVENT, onEvent);
    window.removeEventListener("storage", onStorage);
  };
};

const encodeKey = (key) => key.split("/").map((part) => encodeURIComponent(part).replace(/[!'()*]/g,
  (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`)).join("/");
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Update open editors without replacing user text or resetting unsaved form state.
export const rewriteAssetReferences = (value, moves) => {
  const byKey = new Map(moves.map((move) => [move.source_key, move]));
  const byUrl = new Map();
  moves.forEach((move) => {
    const base = move.old_url.slice(0, -encodeKey(move.source_key).length);
    [move.old_url, base + move.source_key, base + encodeURI(move.source_key)].forEach((url) => {
      byUrl.set(url, move.new_url);
      byUrl.set(url.replace(/%[0-9A-F]{2}/g, (part) => part.toLowerCase()), move.new_url);
    });
  });
  const pattern = new RegExp(`(?:${[...byUrl.keys()].sort((a, b) => b.length - a.length).map(escapeRegExp).join("|")})(?=$|[\\s<>"'\u0060\\)\\]\\}?#])`, "g");
  const rewrite = (item, field = "") => {
    if (typeof item === "string") {
      if (["storage_key", "storageKey"].includes(field) && byKey.has(item)) return byKey.get(item).target_key;
      return item.replace(pattern, (url) => byUrl.get(url));
    }
    if (Array.isArray(item)) return item.map((child) => rewrite(child));
    if (item && typeof item === "object") return Object.fromEntries(Object.entries(item).map(([key, child]) => [key, rewrite(child, key)]));
    return item;
  };
  return moves.length ? rewrite(value) : value;
};
